/**
 * SLIP-10 hierarchical deterministic key derivation for Ed25519.
 *
 * Reference: https://github.com/satoshilabs/slips/blob/master/slip-0010.md
 *
 * For Ed25519, only HARDENED indices (>= 0x80000000) are supported. Public
 * derivation is impossible due to how Ed25519 keys are clamped, so every
 * level in the path must be hardened (suffix `'`).
 *
 * Default path is `m/44'/501'/0'/0'`, the same convention used by Solana
 * and Phantom for crypto-auth keys (BIP-44 with coin_type 501 = Solana).
 * This is purely a convention — the backend does not care about the path,
 * it only checks signatures with the resulting public key.
 *
 * Kept in sync with `client/src/crypto/keys.ts` — change both together.
 */

import { ed25519 } from "@noble/curves/ed25519.js";
import { hmac } from "@noble/hashes/hmac.js";
import { sha512 } from "@noble/hashes/sha2.js";

const HARDENED_OFFSET = 0x80000000;
const ED25519_HMAC_KEY = new TextEncoder().encode("ed25519 seed");

interface HDNode {
  /** 32-byte secret key (the EdDSA seed for this node). */
  secretKey: Uint8Array;
  /** 32-byte chain code. */
  chainCode: Uint8Array;
}

export interface KeyPair {
  /** 32-byte Ed25519 secret key. NEVER send this to a server. */
  secretKey: Uint8Array;
  /** 32-byte Ed25519 public key. Safe to share. */
  publicKey: Uint8Array;
}

export const DEFAULT_DERIVATION_PATH = "m/44'/501'/0'/0'";

function masterFromSeed(seed: Uint8Array): HDNode {
  if (seed.length < 16 || seed.length > 64) {
    throw new Error(`Seed length must be 16..64 bytes, got ${seed.length}`);
  }
  const I = hmac(sha512, ED25519_HMAC_KEY, seed);
  return { secretKey: I.slice(0, 32), chainCode: I.slice(32, 64) };
}

function deriveChild(parent: HDNode, index: number): HDNode {
  if (!Number.isInteger(index) || index < 0 || index > 0xffffffff) {
    throw new RangeError(`Invalid path index: ${index}`);
  }
  if (index < HARDENED_OFFSET) {
    throw new Error(
      "SLIP-10 ed25519 only supports hardened derivation (index must be >= 0x80000000)",
    );
  }

  const data = new Uint8Array(1 + 32 + 4);
  data[0] = 0x00;
  data.set(parent.secretKey, 1);
  data[33] = (index >>> 24) & 0xff;
  data[34] = (index >>> 16) & 0xff;
  data[35] = (index >>> 8) & 0xff;
  data[36] = index & 0xff;

  const I = hmac(sha512, parent.chainCode, data);
  return { secretKey: I.slice(0, 32), chainCode: I.slice(32, 64) };
}

function parsePath(path: string): number[] {
  const segments = path.split("/");
  const head = segments[0];
  if (head !== "m") {
    throw new Error(`Derivation path must start with 'm', got '${head}'`);
  }
  return segments.slice(1).map((segment) => {
    if (!segment.endsWith("'")) {
      throw new Error(
        `SLIP-10 ed25519 requires every segment to be hardened (end with '): '${segment}'`,
      );
    }
    const raw = segment.slice(0, -1);
    const num = Number.parseInt(raw, 10);
    if (!Number.isInteger(num) || num < 0 || num >= HARDENED_OFFSET) {
      throw new Error(`Invalid path segment '${segment}'`);
    }
    return (num + HARDENED_OFFSET) >>> 0;
  });
}

/**
 * Derive an Ed25519 keypair from a BIP39 seed and a SLIP-10 path.
 *
 * The same `seed + path` always yields the same keypair (deterministic),
 * which is what enables multi-device login from the recovery phrase.
 */
export function deriveKeyPair(
  seed: Uint8Array,
  path: string = DEFAULT_DERIVATION_PATH,
): KeyPair {
  const indices = parsePath(path);
  let node = masterFromSeed(seed);
  for (const index of indices) {
    node = deriveChild(node, index);
  }
  const publicKey = ed25519.getPublicKey(node.secretKey);
  return { secretKey: node.secretKey, publicKey };
}
